/**
 * Config-driven: un objeto por recurso simple, consumido por `ResourcePage.jsx`.
 * Se va llenando conforme cada backend de dominio queda listo (ver INVENTARIO.md).
 * Los catálogos de un solo campo (Fase 1) comparten prácticamente la misma config.
 *
 * Este archivo mantiene extensión `.js` (no `.jsx`) porque `App.jsx` lo importa con
 * la extensión explícita (`from './config/resources.js'`) — para el botón "Permisos"
 * de Usuarios (hueco real: backend `PATCH /usuarios/:id/permisos` ya existía, faltaba
 * la UI, ver PLAN.md) se usa `createElement` en vez de JSX, así no hace falta tocar
 * la extensión ni la config de esbuild/vite.
 *
 * Auditoría V1 vs V2 (2026-10-05, lote Catálogos y Usuarios): formularios, etiquetas,
 * orden, requeridos, valores por defecto, botones por fila y filtros igualados a los
 * `Listado*.jsx` / `*Create.jsx` de V1.
 */
import { createElement } from 'react';
import PermisosButton from '../pages/usuarios/PermisosButton.jsx';

// Catálogos simples de V1 (`Listado*.jsx` + `*Create.jsx`): tabla Nombre + Editar/Baja,
// SIN orden por columna ni PDF/Excel; el alta es un formulario de un solo campo
// (etiqueta = singular, ej. "Articulo") y el modal de edición tiene un solo campo
// "Nombre". V1 lista activos E inactivos (el botón de Baja pasa a Alta).
const catalogoSimple = (route, title, recurso, v1) => ({
  route, title, recurso, sortBy: 'name', sortOrder: 'asc', mostrarInactivos: true,
  tituloPagina: v1.pagina, tituloAlta: v1.alta, tituloEdicion: v1.edicion,
  columns: [
    { key: 'name', label: 'Nombre' },
  ],
  fields: [{ key: 'name', label: v1.campo, type: 'text', required: true, placeholder: v1.placeholder }],
  editFields: [{ key: 'name', label: 'Nombre', type: 'text', required: true }],
  ...(v1.extra || {}),
});

// Navieras / Agentes / Forwarders: V1 usa el mismo modelo `Proveedores` filtrado por
// `tipo` (GET .../proveedoresTipo/:tipo); las 3 vistas son idénticas: tabla Nombre +
// Baja (Navieras ni siquiera tiene el botón Editar: está comentado en
// `ListadoNavieras.jsx`), alta de un solo campo que fija `tipo`.
const proveedorPorTipo = (tipo, plural, recurso, extra = {}) => ({
  route: 'proveedores', title: tipo, recurso, sortBy: 'name', sortOrder: 'asc', mostrarInactivos: true,
  extraParams: { tipo },
  tituloPagina: plural,
  tituloAlta: `Crea ${tipo === 'Naviera' ? 'una nueva' : 'un nuevo'} ${tipo}`,
  tituloEdicion: `Editar ${tipo}`,
  columns: [{ key: 'name', label: 'Nombre' }],
  fields: [{ key: 'name', label: tipo, type: 'text', required: true }],
  editFields: [{ key: 'name', label: 'Nombre', type: 'text', required: true }],
  transformBeforeSave: (valores, { editando }) => (editando ? valores : { ...valores, tipo }),
  ...extra,
});

const TIPOS_PROVEEDOR = ['Agente', 'Forwarder', 'Naviera', 'Admin'].map((v) => ({ value: v, label: v }));

export const RESOURCES = {
  articulos: catalogoSimple('articulos', 'Artículo', 'articulos', {
    pagina: 'Articulos', alta: 'Crea un Nuevo Articulo', edicion: 'Editar Articulo', campo: 'Articulo',
  }),
  empresas: catalogoSimple('empresas', 'Empresa', 'empresas', {
    pagina: 'Empresas', alta: 'Crea una nueva Empresa', edicion: 'Editar Empresa', campo: 'Empresa',
  }),
  terminales: catalogoSimple('terminales', 'Terminal', 'terminales', {
    pagina: 'Terminales', alta: 'Crea una nueva Terminal', edicion: 'Editar Terminal', campo: 'Terminal',
    extra: { ocultarIds: ['659751b809da69446cc05f3d'] }, // V1 oculta este registro del listado
  }),
  programas: catalogoSimple('programas', 'Programa', 'programas', {
    pagina: 'Programas', alta: 'Crea una nueva Programa', edicion: 'Editar Programa', campo: 'Programa',
    extra: { ocultarIds: ['65984b7d111f341061f6c353'] }, // V1 oculta este registro del listado
  }),
  patentes: catalogoSimple('patentes', 'Patente', 'patentes', {
    pagina: 'Patentes', alta: 'Crea una nueva Patente', edicion: 'Editar Patente', campo: 'Patente',
    extra: { ocultarIds: ['65984b7d111f341061f6c353'] }, // V1 oculta este registro del listado
  }),
  // V1 (`ListadoConceptosGastos.jsx`): NO pinta el botón <Baja/>, solo Editar.
  'conceptos-gastos': catalogoSimple('conceptos-gastos', 'Concepto de Gasto', 'conceptos-gastos', {
    pagina: 'Conceptos Gastos', alta: 'Crea un Concepto', edicion: 'Editar Conceptos Gastos', campo: 'Concepto',
    placeholder: 'Nombre del Concepto', extra: { sinBaja: true },
  }),

  clientes: {
    route: 'clientes', title: 'Cliente', recurso: 'clientes', sortBy: 'nombre_comercial', sortOrder: 'asc', exportable: true,
    mostrarInactivos: true, filtroActivo: true,
    tituloPagina: 'Clientes', tituloAlta: 'Nuevo Cliente',
    tituloEdicion: (r) => `Editar Cliente ${r.nombre_comercial || ''}`,
    // Columnas y orden 1:1 con el <tbody> real de V1 (`ListadoClientes.jsx` L560-614):
    // Nombre Comercial, Contacto, Telefono, Email, Acciones (Telefono NO sortable).
    columns: [
      { key: 'nombre_comercial', label: 'Nombre Comercial', sortable: true },
      { key: 'contacto', label: 'Contacto', sortable: true },
      { key: 'telefono', label: 'Telefono' },
      { key: 'email', label: 'Email', sortable: true },
    ],
    // PDF/Excel de V1: solo Nombre / Telefono / Mail (respeta el filtro Activo).
    exportColumns: [
      { key: 'nombre_comercial', label: 'Nombre' },
      { key: 'telefono', label: 'Telefono' },
      { key: 'email', label: 'Mail' },
    ],
    tituloExport: 'Listado Clientes', exportFileName: 'Clientes',
    // eMail de V1 (`ListadoClientes.jsx#enviaMail`): mismo PDF, destinatario inicial vacío.
    emailExport: { titulo: 'Enviar Listado Clientes', subject: 'Listado Clientes', fileName: 'ListadoClientes.pdf', cuerpo: 'Adjunto encontraras el listado de Clientes.' },
    formWidth: 'max-w-6xl',
    // Formulario: mismo orden, etiquetas, anchos de fila (`<Col md={n}>`), `required` en
    // TODOS los campos y valores por defecto de `ClientesCreate.jsx`.
    defaultsAlta: {
      contacto: 'NA', nombre_comercial: 'NA', razon_social: 'NA', RFC: 'NA', calle: 'NA',
      numero_ext: 'NA', numero_int: 'NA', pais: 'Mexico', estado: 'NA', ciudad: 'NA',
      delegacion: 'NA', colonia: 'NA', cp: '0', telefono: '0', email: 'a@a.c',
      email2: 'a@a.c', email3: 'a@a.c', observaciones: 'NA',
    },
    fields: [
      { key: 'contacto', label: 'Contacto', type: 'text', required: true, col: 3 },
      { key: 'nombre_comercial', label: 'Nombre Comercial', type: 'text', required: true, col: 3 },
      { key: 'razon_social', label: 'Razon Social', type: 'text', required: true, col: 3 },
      { key: 'RFC', label: 'RFC', type: 'text', required: true, col: 3 },
      { key: 'calle', label: 'Calle', type: 'text', required: true, col: 6 },
      { key: 'numero_ext', label: 'No. Ext', type: 'text', required: true, col: 2 },
      { key: 'numero_int', label: 'No. Int', type: 'text', required: true, col: 2 },
      { key: 'pais', label: 'Pais', type: 'text', required: true, col: 2 },
      { key: 'estado', label: 'Estado', type: 'text', required: true, col: 3 },
      { key: 'ciudad', label: 'Ciudad', type: 'text', required: true, col: 3 },
      { key: 'delegacion', label: 'Delegacion', type: 'text', required: true, col: 3 },
      { key: 'colonia', label: 'Colonia', type: 'text', required: true, col: 3 },
      { key: 'cp', label: 'CP', type: 'text', required: true, col: 3 },
      { key: 'telefono', label: 'Telefono', type: 'text', required: true, col: 3 },
      { key: 'email', label: 'Email', type: 'email', required: true, col: 3 },
      { key: 'email2', label: 'Email 2', type: 'email', required: true, col: 3 },
      { key: 'email3', label: 'Email 3', type: 'email', required: true, col: 3 },
      { key: 'observaciones', label: 'Observaciones', type: 'text', required: true, col: 3 },
    ],
  },

  // V1 (`ListadoProveedores.jsx`): tabla Nombre + Tipo (sin orden), alta con "Proveedor" +
  // "Tipo" (select Selecciona/Agente/Forwarder/Naviera/Admin), edición con "Nombre" + "Tipo".
  proveedores: {
    route: 'proveedores', title: 'Proveedor', recurso: 'proveedores', sortBy: 'name', sortOrder: 'asc', mostrarInactivos: true,
    ocultarIds: ['687f37672fbefb32a7ccdb10'], // V1 oculta este proveedor del listado (siguen en selects)
    tituloPagina: 'Proveedores', tituloAlta: 'Crea un nuevo Proveedor', tituloEdicion: 'Editar Proveedor',
    columns: [
      { key: 'name', label: 'Nombre' },
      { key: 'tipo', label: 'Tipo' },
    ],
    fields: [
      { key: 'name', label: 'Proveedor', type: 'text', required: true },
      { key: 'tipo', label: 'Tipo', type: 'select', required: true, placeholder: 'Selecciona', options: TIPOS_PROVEEDOR },
    ],
    editFields: [
      { key: 'name', label: 'Nombre', type: 'text', required: true },
      { key: 'tipo', label: 'Tipo', type: 'select', required: true, options: TIPOS_PROVEEDOR },
    ],
  },
  navieras: proveedorPorTipo('Naviera', 'Navieras', 'navieras', { sinEditar: true }),
  agentes: proveedorPorTipo('Agente', 'Agentes', 'proveedores'),
  forwarders: proveedorPorTipo('Forwarder', 'Forwarders', 'proveedores'),

  usuarios: {
    route: 'usuarios', title: 'Usuario', recurso: 'usuarios', sortBy: 'apellido', sortOrder: 'asc', mostrarInactivos: true,
    tituloPagina: 'Usuarios', tituloAlta: 'Nuevo Usuario',
    tituloEdicion: (r) => `Editar Usuario ${r.nombre || ''} ${r.apellido || ''}`,
    // Columnas 1:1 con V1 (`ListadoUsuarios.jsx#headers`): Activo (valor crudo Si/No) /
    // Nombre / Apellido / Email, las 4 sortable.
    columns: [
      { key: 'is_active', label: 'Activo', sortable: true, render: (r) => r.is_active || 'Si' },
      { key: 'nombre', label: 'Nombre', sortable: true },
      { key: 'apellido', label: 'Apellido', sortable: true },
      { key: 'email', label: 'Email', sortable: true },
    ],
    // Alta (`Signup.jsx`): Nombre, Apellido, Email, Telefono (numérico), Contraseña; el
    // `tipo` va fijo en "Admin" (el <select> de tipo está comentado en V1).
    fields: [
      { key: 'nombre', label: 'Nombre', type: 'text', required: true },
      { key: 'apellido', label: 'Apellido', type: 'text', required: true },
      { key: 'email', label: 'Email', type: 'email', required: true },
      { key: 'telefono', label: 'Telefono', type: 'number', required: true },
      { key: 'password', label: 'Contraseña', type: 'password', required: true },
    ],
    // Edición (modal de V1): solo Nombre, Apellido y Email.
    editFields: [
      { key: 'nombre', label: 'Nombre', type: 'text', required: true },
      { key: 'apellido', label: 'Apellido', type: 'text', required: true },
      { key: 'email', label: 'Email', type: 'email', required: true },
    ],
    transformBeforeSave: (valores, { editando }) => (editando ? valores : { ...valores, tipo: 'Admin' }),
    // Botón "Permisos" (llave) -- abre `UsuarioPermisosModal` (flags reales de
    // `IbuBack2/src/core/auth/permisos.map.js`, PATCH /usuarios/:id/permisos).
    extraRowActions: (row, { recargar, hasPermiso }) =>
      createElement(PermisosButton, { key: 'permisos', row, recargar, hasPermiso }),
  },
};

export default RESOURCES;
